import './user-pages.sass';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from "react-router-dom";
import useUserCollection from '../hooks/useUserCollection';
import useUserAssets from '../hooks/useUserAssets';
import UserAssetCreate from './UserAssetCreate';
import UserAssetUpdate from './UserAssetUpdate';
import useCheckVideo from '../hooks/useCheckVideo';
import useAlert from '../hooks/useAlert';
import useConfirm from '../hooks/useConfirm';
import { Menu, MenuItem } from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/index.css';
import '@szhsin/react-menu/dist/transitions/slide.css';
import { useTranslation } from 'react-i18next';
import { MdMoreVert, MdOutlineEdit, MdDeleteOutline } from "react-icons/md";

const getVideoId = (url) => {
	const regex = /^https:\/\/www\.(youtube|youtube-nocookie)\.com\/(embed\/|watch\?v=)([a-zA-Z0-9_-]{11})(\?si=[a-zA-Z0-9_-]+)?$/;
	const m = regex.exec(url);
	if (m !== null) {
		return m[3];
	}
	return null;
}

const checkYouTubeVideo = (urlToValidate) => {
	// 正規式
	const regex = /^https:\/\/www\.(youtube|youtube-nocookie)\.com\/(embed\/|watch\?v=)([a-zA-Z0-9_-]{11})(\?si=[a-zA-Z0-9_-]+)?$/;

	// 驗證
	if (regex.test(urlToValidate)) {
		return true;
	}
	
	return false;
};

const Row = ({ asset, setShowUserAssetUpdate, setChosenUserAsset, getUserAssets, getUserCollection }) => {
	const { t, i18n } = useTranslation();
	const { sendAlert } = useAlert();
	const { sendConfirm } = useConfirm();
	const { collectionId, id, assetId, fullName, uri, available, valuePrice, ownerAddress } = asset;
	const { isVideo, mediaType, checkForVideo } = useCheckVideo();
	const isOwner = ownerAddress.toLowerCase() === localStorage.getItem('walletAddress');
	
	useEffect(() => {
		if (uri) checkForVideo(uri);
	}, [uri, checkForVideo]);

	let statusText = '';
	if (isOwner) {
		if (available) {
			statusText = t('user_collections.table_body_status_open');
		} else {
			statusText = t('user_collections.table_body_status_close');
		}
	} else {
		statusText = t('user_collections.table_body_status_sold');
	}

	const confirmBurn = () => {
		return sendConfirm({
			title: `${ t('user_collections.msg_confirm_burn') } ${fullName} ？`,
			onConfirm: () => {
				burnAsset();
			}
		});
	}

	const burnAsset = () => {
		const token = localStorage.getItem('token');
		axios.delete(`items/assets/burn_asset/${id}`, {
			headers: {
				Authorization: `Bearer ${ token }`,
			}
		}).then((response) => {
			const message = response.data.message;
			if (message === 'success') {
				sendAlert(t('user_collections.msg_confirm_burn_ok'));
				getUserCollection();
				getUserAssets();
			}
		}).catch((error) => {
			const message = error.response.data.message;
			sendAlert(t('user_collections.msg_confirm_burn_fail'));
		});
	}

	return (
		<tr>
			<td className='left'>
				<div className='flex'>
					<div className='cover' style={{ backgroundImage: `url(${uri})` }} onContextMenu={(e) => e.preventDefault()}>
						{isVideo ? (
							<video src={`${uri}#t=0.15`} preload='metadata' muted controlsList="nodownload"></video>
						) : (checkYouTubeVideo(uri.replace(/\s/g, '')) ? (
							<iframe 
								className=''
								width="560" 
								height="315" 
								src={`https://www.youtube.com/embed/${getVideoId(uri.replace(/\s/g, ''))}`}
								title="YouTube video player" 
								frameBorder="0" 
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
								allowFullScreen>
							</iframe>
						) : null)}
					</div>
					<div className='name'><Link to={ `/collection/${collectionId}/${id}` }>{fullName}</Link></div>
				</div>
			</td>
			<td className='right nowrap'><b>{valuePrice} P</b></td>
			<td className='left nowrap blue'>{ownerAddress.slice(2, 8).toLocaleUpperCase()}</td>
			<td className='right nowrap'>{statusText}</td>
			<td className='center'>
				{isOwner ? (
				<Menu menuButton={<button className="actions-menu-btn" type="button"><MdMoreVert size={30}/></button>} transition>
					
					<MenuItem onClick={(e) => {
						setShowUserAssetUpdate(true);
						setChosenUserAsset(asset);
					}}><MdOutlineEdit size={20} style={{ marginRight: '.67rem' }}/> {t('user_collections.btn_edit_item')}</MenuItem>
					<MenuItem onClick={confirmBurn}><MdDeleteOutline size={20} style={{ marginRight: '.67rem' }}/> {t('user_collections.btn_burn_item')}</MenuItem>
				</Menu>) : null}
			</td>
		</tr>
	)
}

const UserCollection = () => {
	const { t, i18n } = useTranslation();
	const { id } = useParams();
	const { userCollection, getUserCollection } = useUserCollection(); 
	const { userAssets, getUserAssets } = useUserAssets();
	const { sendAlert } = useAlert();

	const isEmpty = useMemo(() => {
		return !userAssets.length;
	}, [userAssets.length]);

	const getUserAssetsCallback = useCallback(() => {
		getUserAssets({ collectionId: id });
	}, [getUserAssets, id]);

	const getUserCollectionCallback = useCallback(() => {
		getUserCollection({ collectionId: id });
	}, [getUserAssets, id]);

	useEffect(() => {
		getUserAssetsCallback();
		getUserCollectionCallback();
	}, [getUserAssetsCallback, getUserCollectionCallback, id]);

	const [showUserAssetCreate, setShowUserAssetCreate] = useState(false);

	const [showUserAssetUpdate, setShowUserAssetUpdate] = useState(false);

	const [chosenUserAsset, setChosenUserAsset] = useState({});

	return (
		<div id='UserCollection' className='user-pages'>
			{isEmpty ? (
				<div className='wrap'>
					<div className='page-title'><Link to='/user/collections'>{t('user_collections.nav_root_title')}</Link> &gt; {userCollection.fullName}</div>

					<div className='page-content'>
						<div className='empty-wrap'>
							<div className='title'>{t('user_collections.msg_empty')}</div>

							<div className='button-add' onClick={() => {
								if (localStorage.getItem('accountRole') == 'customer') {
									sendAlert(t('user_collections.msg_no_perm'));
									return false;
								}
								setShowUserAssetCreate(true);
								setChosenUserAsset({});
							}}>
								<div className='icon'></div>
								<div className='text'>{t('user_collections.btn_create_item')}</div>
							</div>
						</div>
					</div>

					<div className='monetization-block'>Google ad</div>
				</div>
			) : (
				<div className='position-wrap'>
					<div className='button-add' onClick={() => {
						if (localStorage.getItem('accountRole') == 'customer') {
							sendAlert(t('user_collections.msg_no_perm'));
							return false;
						}
						setShowUserAssetCreate(true);
						setChosenUserAsset({});
					}}>
						<div className='icon'></div>
						<div className='text'>{t('user_collections.btn_create_item')}</div>
					</div>
					<div className='wrap'>
						<div className='page-title'><Link to='/user/collections'>{t('user_collections.nav_root_title')}</Link> &gt; {userCollection.fullName}</div>
						{/* <table>
							<thead>
								<tr>
									<th className='center nowrap' style={{ width: 'calc(100%/3)' }}>可販售項目</th>
									<th className='center nowrap' style={{ width: 'calc(100%/3)' }}>總項目</th>
									<th className='center nowrap' style={{ width: 'calc(100%/3)' }}>擁有者</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td className='center bold'>{ userCollection.onSellAmount }</td>
									<td className='center bold'>{ userCollection.totalCount }</td>
									<td className='center bold'>{ userCollection.owners?.length }</td>
								</tr>
							</tbody>
						</table> */}
						<div className='page-content'>
							<table>
								<thead>
									<tr>
										<th className='left grow'>{t('user_collections.table_header_item')}</th>
										<th className='right nowrap'>{t('user_collections.table_header_price')}</th>
										<th className='left nowrap'>{t('user_collections.table_header_owner')}</th>
										<th className='right nowrap'>{t('user_collections.table_header_status')}</th>
										<th className='center nowrap'>{t('user_collections.table_header_action')}</th>
									</tr>
								</thead>

								<tbody>
									{userAssets.map((asset) => {
										const { id } = asset;
										return (
											<Row key={id} asset={asset} setShowUserAssetUpdate={setShowUserAssetUpdate} setChosenUserAsset={setChosenUserAsset} getUserAssets={getUserAssetsCallback} getUserCollection={getUserCollectionCallback} />
										)
									})}
								</tbody>
							</table>
						</div>

						<div className='monetization-block'>Google ad</div>
					</div>
				</div>
			)}

			<UserAssetCreate show={showUserAssetCreate} close={() => {
				setShowUserAssetCreate(false);
			}} collection={userCollection} getUserAssets={getUserAssetsCallback} getUserCollection={getUserCollectionCallback} />

			<UserAssetUpdate show={showUserAssetUpdate} close={() => {
				setShowUserAssetUpdate(false);
			}} getUserAssets={getUserAssetsCallback} chosenUserAsset={chosenUserAsset} getUserCollection={getUserCollectionCallback} />
		</div>
	)
}

export default UserCollection;